/**
 * SGNL-247 — Pipeline Pacing Dashboard report view.
 *
 * Surfaces the report whose BE shipped in SGNL-243 (PR #158). Replicates
 * Ketan Somani's CY26 Success Planning weekly review sheet inside Signal:
 *
 *   1. Revenue bridge KPI strip (ITB / Qualified F / Unqualified /
 *      Visibility / Go-Get / Coverage)
 *   2. Three weekly target callouts (Generation / Qualification / Conversion)
 *   3. Three per-phase weekly pacing tables
 *   4. Last-4-weeks achievement-vs-target scorecard
 *
 * The BE prerenders an email-HTML version of the same content; this
 * file is the INLINE in-app preview (not the email body). Both consume
 * the same JSON payload so they can never drift.
 *
 * Race safety: a monotonic `_runSeq` counter mirrors the SGNL-246
 * pattern. Toggling the budget input or BU scope cancels any in-flight
 * fetch by id; only the latest one is allowed to commit.
 */

import type { Chart } from "chart.js/auto";

import { fetchPipelinePacingReport, type ReportRunResponse } from "../api";
import { esc, fmtMoney } from "../lib/format";
import type {
  PipelinePacingPhase,
  PipelinePacingReportData,
  PipelinePacingScorecard,
} from "../types";
import {
  destroyChart,
  renderPhaseChart,
  renderScorecardDonut,
} from "./pipeline_pacing_charts";

// Module-level state — persists across re-renders within the same tab
// mount. The reports tab boots once per nav, so this is a single
// reasonable session of the report under a single host element.
// Multi-host scenarios (split-pane Reports view) would need a
// host-keyed Map; today's SPA shape has one host per mount.
interface PacingReportState {
  budget: number | null;
  result: ReportRunResponse<PipelinePacingReportData> | null;
  error: string | null;
  loading: boolean;
  /** Chart.js instances owned by the current render. Destroyed before
   * each new `_renderResult` so DOM canvases don't leak (matches the
   * SGNL-230 `_drillChart` lifecycle pattern). */
  charts: Chart[];
}

const state: PacingReportState = {
  budget: null,
  result: null,
  error: null,
  loading: false,
  charts: [],
};

/** Tear down all Chart.js instances and clear the registry. Called
 * before any DOM rewrite so canvases never reference detached charts. */
function _destroyCharts(): void {
  for (const c of state.charts) destroyChart(c);
  state.charts = [];
}

/** Test seam — mirrors the SGNL-230 pattern in revenue_calendar.ts. */
export function _resetPipelinePacingReportState(): void {
  _destroyCharts();
  state.budget = null;
  state.result = null;
  state.error = null;
  state.loading = false;
  _runSeq = 0;
}

/**
 * Public teardown — call from the router when the user navigates away
 * from the Reports tab so the Chart.js instances are released instead
 * of held in the heap until the next re-mount (SEC-003 pre-PR — bounded
 * memory leak, not a confidentiality issue). Same effect as the test
 * seam without resetting the budget/result state.
 */
export function teardownPipelinePacingReport(): void {
  _destroyCharts();
}

// Monotonic counter so a slower stale fetch doesn't overwrite the
// latest committed result. Same pattern as the SGNL-246 pacing summary.
let _runSeq = 0;

/** Public entry point — render the report into the supplied host. */
export async function renderPipelinePacingReport(host: HTMLElement): Promise<void> {
  // Boot the shell once. Subsequent budget-input changes only touch
  // the inner result region so the input doesn't blur mid-typing.
  host.innerHTML = `
    <section class="pacing-report" data-testid="pacing-report">
      <header class="pacing-report__header">
        <h3 class="pacing-report__title">Pipeline Pacing Dashboard</h3>
        <p class="pacing-report__lead muted">
          Weekly Generation / Qualification / Conversion pacing against
          Bounteous's CY budget. Mirrors Ketan Somani's NMC review sheet.
        </p>
      </header>
      <div class="pacing-report__controls" data-testid="pacing-report-controls">
        <label class="pacing-report__budget-label">
          Annual budget ($)
          <input
            type="number"
            min="0"
            step="1000000"
            placeholder="e.g. 405000000"
            data-testid="pacing-report-budget"
            value="${state.budget ?? ""}"
          />
        </label>
        <button
          type="button"
          class="pacing-report__refresh"
          data-action="pacing-refresh"
          data-testid="pacing-report-refresh"
        >Refresh</button>
      </div>
      <div class="pacing-report__result" data-testid="pacing-report-result"></div>
    </section>
  `;

  _bindControls(host);

  // Initial fetch — budget=null produces the buckets-only view.
  await _refresh(host);
}

function _bindControls(host: HTMLElement): void {
  const budgetInput = host.querySelector<HTMLInputElement>(
    "[data-testid='pacing-report-budget']",
  );
  budgetInput?.addEventListener("change", () => {
    const raw = budgetInput.value.trim();
    if (raw === "") {
      state.budget = null;
    } else {
      const parsed = Number(raw);
      // Ignore non-finite / negative input — snap back to the prior
      // committed value rather than firing a bad request.
      if (!Number.isFinite(parsed) || parsed < 0) {
        budgetInput.value = state.budget == null ? "" : String(state.budget);
        return;
      }
      // Floor floats to int (BE expects an integer). Mirror the
      // committed value back to the input so the user sees what we
      // actually used (CR-02 pre-PR — float-display-mismatch).
      const floored = Math.floor(parsed);
      state.budget = floored === 0 ? null : floored;
      budgetInput.value = state.budget == null ? "" : String(state.budget);
    }
    void _refresh(host);
  });

  host.querySelector<HTMLButtonElement>("[data-action='pacing-refresh']")
    ?.addEventListener("click", () => void _refresh(host));
}

async function _refresh(host: HTMLElement): Promise<void> {
  const seq = ++_runSeq;
  state.loading = true;
  state.error = null;
  // Clear stale result so the spinner doesn't render over previous data.
  state.result = null;
  _renderResult(host);

  try {
    const res = await fetchPipelinePacingReport({
      budget: state.budget == null ? undefined : state.budget,
    });
    // Stale guard — `return` exits the async function entirely,
    // so the settled-state `_renderResult` below is correctly skipped.
    // A stale fetch cannot destroy the live charts owned by the
    // winning render (ARCH-01 was a false positive against this
    // invariant; the race test in pipeline_pacing_report.test.ts
    // pins the behaviour).
    if (seq !== _runSeq) return;
    state.result = res;
    state.loading = false;
  } catch (e) {
    if (seq !== _runSeq) return;
    state.result = null;
    state.loading = false;
    state.error = e instanceof Error ? e.message : String(e);
  }
  _renderResult(host);
}

function _renderResult(host: HTMLElement): void {
  const slot = host.querySelector<HTMLElement>(
    "[data-testid='pacing-report-result']",
  );
  if (!slot) return;
  // Destroy any prior-generation Chart.js instances BEFORE rewriting
  // the slot so they don't keep references to detached canvases.
  _destroyCharts();
  if (state.loading) {
    slot.innerHTML = `<div class="pacing-report__loading muted" data-testid="pacing-report-loading">Loading pipeline pacing…</div>`;
    return;
  }
  if (state.error) {
    slot.innerHTML = `<div class="pacing-report__error" data-testid="pacing-report-error">${esc(state.error)}</div>`;
    return;
  }
  if (!state.result) {
    slot.innerHTML = "";
    return;
  }
  slot.innerHTML = _renderReportHTML(state.result.data);
  _mountCharts(slot, state.result.data);
  // Note: the "Show data table" toggles use native `<details>` /
  // `<summary>` — no JS listeners needed for the disclosure behaviour.
}

/**
 * Walk every chart canvas in the rendered DOM, instantiate the
 * matching Chart.js instance, and track it on `state.charts` for
 * later teardown. Called AFTER the slot's innerHTML is set so the
 * canvases exist; called BEFORE the user can interact so animations /
 * race-toggles can't fire against detached charts.
 *
 * Test-environment note: jsdom has no Canvas2D context. The chart
 * instantiation throws there; per-canvas errors are caught + logged
 * (NOT silently swallowed) so a production Chart.js regression
 * surfaces in DevTools/Sentry while the user still sees the
 * collapsible data table below as the fallback.
 *
 * Renamed from `_instantiateCharts` (CR-05 pre-PR) — `mount` is the
 * verb that matches the DOM-query + construct + register lifecycle.
 */
function _mountCharts(slot: HTMLElement, data: PipelinePacingReportData): void {
  for (const phase of ["generation", "qualification", "conversion"] as const) {
    // ARCH-05 pre-PR: canvas absence IS the empty-state signal — the
    // template (`_phaseChartBlock`) renders a `<p>` fallback instead
    // of the canvas when weeks is empty, so this query returns null
    // for empty phases. The prior redundant `p.weeks.length === 0`
    // check was a second guard expressing the same invariant; the
    // single-source-of-truth lives in `_phaseChartBlock`.
    const canvas = slot.querySelector<HTMLCanvasElement>(
      `[data-testid='pacing-chart-${phase}']`,
    );
    if (!canvas) continue;
    const p = data.phases[phase];
    try {
      state.charts.push(renderPhaseChart(canvas, p));
    } catch (e) {
      // jsdom or a Chart.js failure. Log + continue — the
      // collapsible data table below carries the same numbers, so
      // the user never loses data, but the error reaches DevTools /
      // Sentry instead of being silently swallowed (CR-02 pre-PR).
      // eslint-disable-next-line no-console
      console.error(`[pacing-report] Chart mount failed for phase=${phase}:`, e);
    }
  }
  for (const sc of data.last_4_weeks) {
    const canvas = slot.querySelector<HTMLCanvasElement>(
      `[data-testid='pacing-donut-${sc.phase}']`,
    );
    if (!canvas) continue;
    try {
      state.charts.push(renderScorecardDonut(canvas, sc));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[pacing-report] Donut mount failed for phase=${sc.phase}:`, e);
    }
  }
}

// ---------------------------------------------------------------------------
// Block renderers — each one returns an HTML fragment string.
// ---------------------------------------------------------------------------

function _renderReportHTML(d: PipelinePacingReportData): string {
  const h = d.headline;
  return `
    <div class="pacing-report__year-line" data-testid="pacing-report-year">
      CY${d.year} · ${d.bu_scope.length === 0 ? "All BUs" : esc(d.bu_scope.join(", "))} · Budget ${d.budget == null ? "—" : esc(fmtMoney(d.budget))}
    </div>

    <h4 class="pacing-report__h4">Revenue bridge</h4>
    <div class="pacing-report__kpis" data-testid="pacing-report-kpis">
      ${_pacingTile({ family: "indigo", base: "pacing-kpi", testid: "pacing-kpi-itb",
        label: "In the Bag", value: fmtMoney(h.itb_total), sub: _oppCountLabel(h.itb_opps) })}
      ${_pacingTile({ family: "purple", base: "pacing-kpi", testid: "pacing-kpi-qualified",
        label: "Qualified (F)", value: fmtMoney(h.qualified_total), sub: _oppCountLabel(h.qualified_opps) })}
      ${_pacingTile({ family: "slate", base: "pacing-kpi", testid: "pacing-kpi-unqualified",
        label: "Unqualified", value: fmtMoney(h.unqualified_total), sub: _oppCountLabel(h.unqualified_opps) })}
      ${_pacingTile({ family: "electric", base: "pacing-kpi", testid: "pacing-kpi-visibility",
        label: "Visibility", value: fmtMoney(h.revenue_visibility), sub: "ITB + Qualified (F)" })}
      ${_pacingTile({ family: "coral", base: "pacing-kpi", testid: "pacing-kpi-go-get",
        label: "Go Get", value: h.go_get == null ? "—" : fmtMoney(h.go_get) })}
      ${_pacingTile({
        family: h.pipeline_coverage != null && h.pipeline_coverage >= h.target_coverage ? "aqua" : "coral",
        base: "pacing-kpi", testid: "pacing-kpi-coverage",
        label: `Coverage (target ${h.target_coverage.toFixed(1)}×)`,
        value: h.pipeline_coverage == null ? "—" : `${h.pipeline_coverage.toFixed(2)}×`,
      })}
    </div>

    <h4 class="pacing-report__h4">Weekly phase targets</h4>
    <div class="pacing-report__targets" data-testid="pacing-report-targets">
      ${_pacingTile({ family: "indigo", base: "pacing-target", testid: "pacing-target-generation",
        label: "Weekly Generation Target", value: fmtMoney(d.phases.generation.next_week_target) })}
      ${_pacingTile({ family: "electric", base: "pacing-target", testid: "pacing-target-qualification",
        label: "Weekly Qualification Target", value: fmtMoney(d.phases.qualification.next_week_target) })}
      ${_pacingTile({ family: "aqua", base: "pacing-target", testid: "pacing-target-conversion",
        label: "Weekly Conversion Target", value: fmtMoney(d.phases.conversion.next_week_target) })}
    </div>

    ${_phaseChartBlock("Generation", d.phases.generation, "generation")}
    ${_phaseChartBlock("Qualification", d.phases.qualification, "qualification")}
    ${_phaseChartBlock("Conversion", d.phases.conversion, "conversion")}

    <h4 class="pacing-report__h4">Last 4 weeks — achievement vs target</h4>
    <div class="pacing-report__scorecard-grid" data-testid="pacing-report-scorecard">
      ${d.last_4_weeks.map(_scorecardDonut).join("")}
    </div>
    <details class="pacing-report__details" data-testid="pacing-report-scorecard-details">
      <summary class="pacing-report__details-summary">Show data table</summary>
      <table class="pacing-report__scorecard-table">
        <thead>
          <tr>
            <th>Phase</th>
            <th class="num">Target</th>
            <th class="num">Actual</th>
            <th class="num">Achievement</th>
          </tr>
        </thead>
        <tbody>
          ${d.last_4_weeks.map(_scorecardRow).join("")}
        </tbody>
      </table>
    </details>

    ${h.excluded_categories.length === 0
      ? ""
      : `<div class="pacing-report__excluded" data-testid="pacing-report-excluded">Excluded pass-through revenue: ${esc(h.excluded_categories.join(", "))}</div>`}
    ${h.snapshot_at == null
      ? ""
      : `<div class="pacing-report__snapshot muted" data-testid="pacing-report-snapshot">Data as of ${esc(h.snapshot_at.slice(0, 19).replace("T", " "))} UTC</div>`}
  `;
}

/**
 * Shared tile renderer for both KPI and weekly-target blocks. CR-07
 * pre-PR called the original `_kpiTile` and `_targetTile` near-duplicates;
 * they emitted the same BEM markup with different class bases. One
 * function with a `base` discriminator removes the duplication and
 * makes a future tile-shape change a one-place edit. `sub` is the
 * opp-count or derivation hint that lives below the value (ARCH-03
 * pre-PR — exposes the otherwise-unrendered `_opps` fields).
 */
function _pacingTile(opts: {
  label: string;
  value: string;
  family: "indigo" | "purple" | "slate" | "electric" | "aqua" | "coral";
  base: "pacing-kpi" | "pacing-target";
  testid: string;
  sub?: string;
}): string {
  const sub = opts.sub
    ? `<div class="${esc(opts.base)}__sub">${esc(opts.sub)}</div>`
    : "";
  return `
    <div class="${esc(opts.base)} ${esc(opts.base)}--${esc(opts.family)}" data-testid="${esc(opts.testid)}">
      <div class="${esc(opts.base)}__label">${esc(opts.label)}</div>
      <div class="${esc(opts.base)}__value">${esc(opts.value)}</div>
      ${sub}
    </div>
  `;
}

function _oppCountLabel(n: number): string {
  return `${n} opp${n === 1 ? "" : "s"}`;
}

/**
 * One phase block — heading + interactive Chart.js bar/line chart +
 * collapsible "Show data" `<details>` containing the same week-by-week
 * table the prior version of this view shipped (SGNL-247). The table
 * stays as the data-dissection surface the user asked for; the chart
 * is what they see by default and matches Ketan's reference.
 *
 * The canvas carries `data-testid="pacing-chart-{phase}"` which the
 * post-render `_instantiateCharts` walks to attach Chart.js instances.
 * If the canvas can't be instantiated (e.g. jsdom in tests), the
 * collapsible table below carries the same numbers, so the user is
 * never left without the data.
 */
function _phaseChartBlock(
  label: string,
  phase: PipelinePacingPhase,
  phaseKey: "generation" | "qualification" | "conversion",
): string {
  const heading = `
    <h4 class="pacing-report__h4">
      ${esc(label)} pacing
      <span class="pacing-report__h4-meta">
        ${esc(String(phase.weeks_remaining))} weeks remaining · next-week target ${esc(fmtMoney(phase.next_week_target))}
      </span>
    </h4>
  `;
  if (phase.weeks.length === 0) {
    return `
      ${heading}
      <p class="muted" data-testid="pacing-chart-${esc(phaseKey)}-empty">
        No snapshot history available yet — pacing will fill in as daily snapshots accrue.
      </p>
    `;
  }
  const rows = phase.weeks
    .map(
      (w) => `
        <tr>
          <td>${esc(w.week_starting)} → ${esc(w.week_ending)}</td>
          <td class="num">${esc(fmtMoney(w.target))}</td>
          <td class="num">${esc(fmtMoney(w.actual))}</td>
        </tr>
      `,
    )
    .join("");
  return `
    ${heading}
    <div class="pacing-report__chart-wrap" data-testid="pacing-chart-${esc(phaseKey)}-wrap">
      <canvas
        class="pacing-report__chart"
        data-testid="pacing-chart-${esc(phaseKey)}"
        aria-label="${esc(label)} weekly actual vs target chart"
      ></canvas>
    </div>
    <details class="pacing-report__details" data-testid="pacing-table-${esc(phaseKey)}-details">
      <summary class="pacing-report__details-summary">Show data table</summary>
      <table class="pacing-report__phase-table" data-testid="pacing-table-${esc(phaseKey)}">
        <thead>
          <tr>
            <th>Week</th>
            <th class="num">Target</th>
            <th class="num">Actual</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </details>
  `;
}

/**
 * One scorecard donut tile — canvas + label + numeric summary. The
 * Chart.js doughnut renders the achievement percentage as the filled
 * arc; the label below + tooltip carry the absolute target/actual.
 */
function _scorecardDonut(s: PipelinePacingScorecard): string {
  // PR-Agent importance 5: same finite-number guard as the donut's
  // chart-layer renderer. A compromised payload with NaN / Infinity
  // would otherwise render "NaN%" / "Infinity%" in the center label,
  // which is visible to the user even though the donut's visual
  // coerces to 0.
  const raw = s.achievement_pct;
  const pct =
    typeof raw === "number" && Number.isFinite(raw) ? `${raw.toFixed(0)}%` : "—";
  return `
    <div class="pacing-report__donut-tile" data-testid="pacing-scorecard-${esc(s.phase)}">
      <div class="pacing-report__donut-label">${esc(s.phase[0].toUpperCase() + s.phase.slice(1))}</div>
      <div class="pacing-report__donut-wrap">
        <canvas
          class="pacing-report__donut-canvas"
          data-testid="pacing-donut-${esc(s.phase)}"
          aria-label="${esc(s.phase)} 4-week achievement donut"
        ></canvas>
        <div class="pacing-report__donut-center">${esc(pct)}</div>
      </div>
      <div class="pacing-report__donut-totals">
        ${esc(fmtMoney(s.actual_total))} / ${esc(fmtMoney(s.target_total))}
      </div>
    </div>
  `;
}

function _scorecardRow(s: PipelinePacingScorecard): string {
  const pct = s.achievement_pct == null ? "—" : `${s.achievement_pct.toFixed(0)}%`;
  // CR-01 pre-PR: row testid uses `pacing-scorecard-row-{phase}` to
  // avoid colliding with the donut TILE testid (`pacing-scorecard-{phase}`)
  // — both elements are live in the DOM simultaneously (tile above
  // `<details>`, row inside it), so a `querySelector` for the row by
  // tile-id would silently return the tile.
  return `
    <tr data-testid="pacing-scorecard-row-${esc(s.phase)}">
      <td>${esc(s.phase[0].toUpperCase() + s.phase.slice(1))}</td>
      <td class="num">${esc(fmtMoney(s.target_total))}</td>
      <td class="num">${esc(fmtMoney(s.actual_total))}</td>
      <td class="num">${esc(pct)}</td>
    </tr>
  `;
}
